import { Box, Flex, Text, CloseButton } from '@chakra-ui/react';
import { formatDistanceToNow } from 'date-fns';
import { Notification } from '../../types/notification';
import { ReactComponent as SignatureIcon } from 'assets/notification-signature-icon.svg';
import { ReactComponent as CompletedIcon } from 'assets/notification-completed-icon.svg';
import { ReactComponent as ConfigIcon } from 'assets/notification-config-icon.svg';
import { ReactComponent as InvitationIcon } from 'assets/notification-invitation-icon.svg';

const notificationIconMap: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  SIGNATURE_NEEDED: SignatureIcon,
  TRANSACTION_COMPLETED: CompletedIcon,
  WALLET_CONFIG_UPDATED: ConfigIcon,
  WALLET_INVITATION: InvitationIcon,
};

const notificationBgMap: Record<string, string> = {
  SIGNATURE_NEEDED: 'orange.50',
  TRANSACTION_COMPLETED: 'green.50',
  WALLET_CONFIG_UPDATED: 'purple.50',
  WALLET_INVITATION: 'blue.50',
};

interface NotificationItemProps {
  notification: Notification;
  onDismiss: (id: string) => void;
  onNavigate: (notification: Notification) => void;
  itemRef?: (el: HTMLDivElement | null) => void;
}

const NotificationItem = ({
  notification,
  onDismiss,
  onNavigate,
  itemRef,
}: NotificationItemProps) => {
  const isSeen = notification.status === 'SEEN';
  const Icon = notificationIconMap[notification.type];
  const iconBg = notificationBgMap[notification.type] || 'blackAlpha.50';

  return (
    <Flex
      ref={itemRef}
      px={4}
      py={3}
      alignItems="flex-start"
      gap={3}
      cursor="pointer"
      opacity={isSeen ? 0.7 : 1}
      bg={isSeen ? 'primary' : 'white'}
      _hover={{ bg: isSeen ? 'blackAlpha.100' : 'blackAlpha.50' }}
      borderBottom="1px solid"
      borderColor="gray.100"
      onClick={() => onNavigate(notification)}
    >
      {Icon && (
        <Flex
          alignItems="center"
          justifyContent="center"
          w={8}
          h={8}
          borderRadius="full"
          bg={iconBg}
          flexShrink={0}
          mt="2px"
        >
          <Icon width={16} height={16} />
        </Flex>
      )}
      <Box flex={1} minW={0}>
        <Text fontSize="sm" noOfLines={2}>
          {notification.message}
        </Text>
        <Text fontSize="xs" color="blackAlpha.500" mt={1}>
          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
        </Text>
      </Box>
      <CloseButton
        aria-label="Dismiss notification"
        size="sm"
        minW="44px"
        minH="44px"
        onClick={e => {
          e.stopPropagation();
          onDismiss(notification._id);
        }}
      />
    </Flex>
  );
};

export default NotificationItem;
